import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Camera, MessageCircle, CalendarCheck } from "lucide-react";
import { SubscribeButton } from "./SubscribeButton";
import { ReferralInput } from "./ReferralInput";

const LINES_DESKTOP = [
  { text: "SEGUIAMO IL TUO", accent: false },
  { text: "MARKETING A", accent: false },
  { text: "997€ ALL'ANNO", accent: true },
];
const LINES_MOBILE = [
  { text: "SEGUIAMO IL", accent: false },
  { text: "TUO", accent: false },
  { text: "MARKETING A", accent: false },
  { text: "997€", accent: true },
  { text: "ALL'ANNO", accent: true },
];

const MaskedLine = ({ text, index, accent = false }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay: 0.25 + index * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`block font-heading text-[2.65rem] font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl ${
        accent ? "text-purple-600" : "text-slate-900"
      }`}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.span
        aria-hidden="true"
        style={{ y: yGhost }}
        className="pointer-events-none absolute -right-10 top-16 select-none font-heading text-[22vw] font-extrabold leading-none text-purple-600/[0.05]"
      >
        MIR-IA
      </motion.span>
      <div className="mx-auto max-w-4xl px-5 pb-14 pt-6 text-center sm:pt-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-tagline"
          className="mb-5 inline-block max-w-full rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-[10px] font-semibold uppercase leading-relaxed tracking-wide text-purple-700 sm:text-xs"
        >
          MIR-IA · La piattaforma dalla parte dei liberi professionisti e piccole attività
        </motion.p>
        <h1 data-testid="hero-title" className="text-center">
          <span className="hidden sm:block">
            {LINES_DESKTOP.map((l, i) => (
              <MaskedLine key={l.text} text={l.text} index={i} accent={l.accent} />
            ))}
          </span>
          <span className="block sm:hidden">
            {LINES_MOBILE.map((l, i) => (
              <MaskedLine key={l.text} text={l.text} index={i} accent={l.accent} />
            ))}
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          data-testid="hero-subtitle"
          className="mt-4 text-lg font-medium text-purple-700 sm:text-xl"
        >
          Meno del costo di una cena al mese.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-7 flex flex-wrap items-center justify-center gap-4"
        >
          <SubscribeButton testid="hero-subscribe-cta-button" />
          <a
            href="#soluzione"
            data-testid="hero-discover-link"
            className="text-sm font-semibold text-slate-600 underline decoration-purple-300 decoration-2 underline-offset-4 transition-colors hover:text-purple-700"
          >
            Scopri cosa include ↓
          </a>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05 }}
          className="mt-4 text-xs text-slate-400"
        >
          Pagamento sicuro con Stripe · Shooting fotografico iniziale incluso · 365 giorni
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
          <ReferralInput suffix="hero" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-9 grid gap-4 sm:grid-cols-3"
          data-testid="hero-cards"
        >
          <div className="rounded-2xl border border-purple-100 bg-white p-5 text-left shadow-lg shadow-purple-600/5">
            <Camera className="mb-2 h-6 w-6 text-purple-600" />
            <p className="font-heading text-base font-bold text-slate-900">Shooting fotografico iniziale</p>
            <p className="mt-1 text-sm text-slate-500">
              È previsto 1 shooting ad inizio percorso per realizzare gli scatti professionali di tutto l'anno.
              Troverai i post pronti da pubblicare in app (con descrizione ed hashtag per la tua azienda)
            </p>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-white p-5 text-left shadow-lg shadow-purple-600/5">
            <MessageCircle className="mb-2 h-6 w-6 text-purple-600" />
            <p className="font-heading text-base font-bold text-slate-900">MIR-IA</p>
            <p className="mt-1.5 rounded-xl rounded-br-sm bg-purple-600 p-3 text-sm text-white">
              Intelligenza artificiale <strong>in collaborazione con Miriana Lomartire</strong> di Mirlo's Company
            </p>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-white p-5 text-left shadow-lg shadow-purple-600/5">
            <CalendarCheck className="mb-2 h-6 w-6 text-purple-600" />
            <p className="font-heading text-2xl font-extrabold text-slate-900">
              997€ <span className="text-sm font-semibold text-slate-400">/ anno!</span>
            </p>
            <p className="mt-1 text-sm text-slate-500">
              La tua presenza online completa a{" "}
              <strong className="text-slate-800">meno del costo di una cena fuori</strong>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
